/**
 * Why a sign-in attempt failed, in the only terms the login form needs.
 *
 * This exists because the form used to collapse EVERY failure that was not one
 * of four hard-coded server strings into "Identifiants invalides" — a dropped
 * 4G connection, a 429 from the rate limiter, Hasura being down. People were
 * told their password was wrong when the request had never reached the server,
 * retried, failed again, and concluded they were locked out of the product.
 * Nothing reached Sentry either, because the error was caught and discarded.
 *
 * The distinction that matters is not fine-grained blame, it is: is this MY
 * fault, is it the network's, or is it the server's — because each one implies
 * a different next move for the person reading it.
 */
export type LoginFailureKind =
  | 'invalid-credentials'
  | 'account-deactivated'
  | 'account-locked'
  | 'rate-limited'
  | 'network'
  | 'server'
  | 'unknown'

export interface LoginFailure {
  kind: LoginFailureKind
  /** The raw server message, kept for logs — never shown as-is to the user. */
  detail?: string
}

/**
 * An urql `CombinedError` carries a `networkError` when the request never came
 * back with a GraphQL body. Typed structurally so this module does not drag in
 * urql, and so a plain `Error` thrown by `fetch` classifies too.
 */
type MaybeCombinedError = {
  message?: string
  networkError?: unknown
  graphQLErrors?: Array<{
    message?: string
    extensions?: Record<string, unknown> | null
  }>
  response?: { status?: number }
}

const TOO_MANY_REQUESTS = /too many requests|rate limit/i

/**
 * `fetch` rejects with a TypeError whose wording is browser-specific, which is
 * exactly the population that reports "it works at the office, not on my
 * phone". Matching on the wording of all of them is a losing game, so this
 * only backs up the `networkError` check above it.
 */
const FETCH_FAILURE = /failed to fetch|networkerror|network request failed|load failed|connection/i

export function classifyLoginFailure(error: unknown): LoginFailure {
  const err = (error ?? {}) as MaybeCombinedError
  const message = typeof err.message === 'string' ? err.message : ''

  // The request never completed. Checked FIRST: a transport failure carries no
  // server verdict, so nothing below it could be true.
  if (err.networkError != null || FETCH_FAILURE.test(message)) {
    return { kind: 'network', detail: message || undefined }
  }

  const status = err.response?.status
  const codes = (err.graphQLErrors ?? []).map((e) => e?.extensions?.code)

  if (status === 429 || TOO_MANY_REQUESTS.test(message)) {
    return { kind: 'rate-limited', detail: message || undefined }
  }

  // The backend answers a lockout with this code as well as the message, so
  // prefer the code — it survives a wording change.
  if (codes.includes('account-locked') || /account temporarily locked/i.test(message)) {
    return { kind: 'account-locked', detail: message || undefined }
  }
  if (/account is deactivated/i.test(message)) {
    return { kind: 'account-deactivated', detail: message || undefined }
  }
  if (/invalid email or password/i.test(message)) {
    return { kind: 'invalid-credentials', detail: message || undefined }
  }
  if (/internal server error/i.test(message) || (status != null && status >= 500)) {
    return { kind: 'server', detail: message || undefined }
  }

  // Deliberately NOT 'invalid-credentials'. An unrecognised failure is the one
  // case where guessing does real damage: telling someone their password is
  // wrong when we do not know that is what sent them round the loop.
  return { kind: 'unknown', detail: message || undefined }
}

/**
 * What the person reads. Each one says what happened and what to do next; none
 * of them blames the password unless the server actually said so.
 */
export const LOGIN_FAILURE_MESSAGES: Record<LoginFailureKind, string> = {
  'invalid-credentials': 'E-mail ou mot de passe incorrect.',
  'account-deactivated':
    'Votre compte a été désactivé. Veuillez contacter votre administrateur.',
  'account-locked':
    'Compte temporairement verrouillé après plusieurs tentatives. Réessayez dans 15 minutes.',
  'rate-limited':
    'Trop de tentatives de connexion depuis votre réseau. Patientez quelques minutes avant de réessayer.',
  network:
    "Connexion au serveur impossible. Vérifiez votre connexion internet et réessayez — vos identifiants n'ont pas été vérifiés.",
  server:
    'Une erreur serveur est survenue. Réessayez dans un instant ; si cela persiste, contactez votre administrateur.',
  unknown:
    "La connexion a échoué pour une raison inattendue. Réessayez ; si cela persiste, contactez votre administrateur.",
}

export const loginFailureMessage = (kind: LoginFailureKind): string =>
  LOGIN_FAILURE_MESSAGES[kind]
